// frontend/src/pages/admin/YouTubeLivePage.js
//
// Admin → YouTube Live. Connect the club channel once, then per match:
// schedule a broadcast with its own stream key, hand the camera team a
// copy-ready block, watch whether their camera is reaching YouTube, and
// press Go live / End. Ending links the recording to the match's scorecard.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const navy = '#1E3A6E';
const card = { background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 14 };
const btn = (bg, fg = 'white', border = 'none') => ({
  padding: '9px 14px', borderRadius: 9, border, background: bg, color: fg,
  fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
});

const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—';
const teams = (m) => (m.capt1_team && m.capt2_team) ? `${m.capt1_team} vs ${m.capt2_team}` : (m.match_type || 'Match');

function StatusChip({ status }) {
  const map = {
    live:     ['● LIVE', '#dc2626', '#fee2e2'],
    created:  ['Scheduled', '#1d4ed8', '#dbeafe'],
    complete: ['Ended', '#374151', '#f3f4f6'],
  };
  const [label, fg, bg] = map[status] || ['No broadcast', '#9ca3af', '#f9fafb'];
  return <span style={{ fontSize: '0.68rem', fontWeight: 800, color: fg, background: bg, padding: '3px 8px', borderRadius: 999 }}>{label}</span>;
}

function CopyRow({ label, value, secret }) {
  const [show, setShow] = useState(!secret);
  const copy = () => navigator.clipboard.writeText(value || '').then(() => toast.success(`${label} copied`));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px dashed #e5e7eb' }}>
      <div style={{ width: 110, fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
        {value ? (show ? value : '•••••••••••••••••') : '—'}
      </div>
      {secret && value && (
        <button onClick={() => setShow(s => !s)} style={btn('white', navy, '1px solid #d1d5db')}>{show ? 'Hide' : 'Show'}</button>
      )}
      {value && <button onClick={copy} style={btn('white', navy, '1px solid #d1d5db')}>Copy</button>}
    </div>
  );
}

function HealthDot({ b }) {
  if (!b) return null;
  if (b.status === 'complete') return null;
  const active = b.stream_status === 'active';
  const colour = !active ? '#9ca3af' : b.health === 'good' || b.health === 'ok' ? '#16a34a' : b.health === 'bad' ? '#dc2626' : '#f59e0b';
  const text = !active
    ? 'Camera not sending yet'
    : `Camera connected — signal ${b.health === 'noData' ? 'starting' : b.health || 'unknown'}`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', fontWeight: 600, margin: '6px 0 10px' }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: colour, display: 'inline-block' }} />
      {text}
    </div>
  );
}

export default function YouTubeLivePage() {
  const [status, setStatus]   = useState(null);   // connection
  const [matches, setMatches] = useState([]);
  const [openId, setOpenId]   = useState(null);
  const [detail, setDetail]   = useState(null);   // broadcast for openId
  const [when, setWhen]       = useState('');
  const [busy, setBusy]       = useState('');
  const poll = useRef(null);

  const loadStatus  = useCallback(() => api.get('/youtube/status').then(r => setStatus(r.data))
    .catch(e => setStatus({ error: e.response?.data?.error || 'Failed to load' })), []);
  const loadMatches = useCallback(() => api.get('/youtube/matches').then(r => setMatches(r.data.matches || []))
    .catch(() => {}), []);
  const loadDetail  = useCallback((id) => api.get(`/youtube/matches/${id}/broadcast`)
    .then(r => setDetail(r.data.broadcast)).catch(e => toast.error(e.response?.data?.error || 'Failed to load broadcast')), []);

  // Result of the Google sign-in comes back on the URL.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('connected')) toast.success('YouTube channel connected');
    if (p.get('error')) toast.error(p.get('error'), { duration: 10000 });
    if (p.get('connected') || p.get('error')) window.history.replaceState({}, '', '/admin/youtube');
    loadStatus(); loadMatches();
  }, [loadStatus, loadMatches]);

  // Watch the camera every 10 s while a broadcast that isn't over is open.
  useEffect(() => {
    clearInterval(poll.current);
    if (openId && detail && detail.status !== 'complete') {
      poll.current = setInterval(() => loadDetail(openId), 10000);
    }
    return () => clearInterval(poll.current);
  }, [openId, detail, loadDetail]);

  function open(m) {
    if (openId === m.id) { setOpenId(null); setDetail(null); return; }
    setOpenId(m.id); setDetail(null);
    setWhen(`${m.match_date}T08:00`);
    if (m.has_stream) loadDetail(m.id);
  }

  async function act(label, fn, okMsg) {
    setBusy(label);
    try { const r = await fn(); if (okMsg) toast.success(typeof okMsg === 'function' ? okMsg(r) : okMsg); return r; }
    catch (e) { toast.error(e.response?.data?.error || 'Failed', { duration: 10000 }); return null; }
    finally { setBusy(''); }
  }

  const connect = () => act('connect', async () => {
    const r = await api.get('/youtube/oauth/start');
    window.location.href = r.data.url;
  });
  const disconnect = () => {
    if (!window.confirm('Disconnect the club YouTube channel? Broadcasts can\'t be created or controlled until it is connected again.')) return;
    act('disconnect', () => api.post('/youtube/disconnect'), 'Disconnected').then(loadStatus);
  };
  const create = (m) => act('create', () => api.post(`/youtube/matches/${m.id}/broadcast`, { scheduled_at: new Date(when).toISOString() }),
    'Broadcast scheduled — send the camera team the details below').then(r => { if (r) { loadMatches(); loadDetail(m.id); } });
  const goLive = (m) => {
    if (!window.confirm('Go live now? The broadcast becomes public on YouTube immediately.')) return;
    act('live', () => api.post(`/youtube/matches/${m.id}/go-live`), '🔴 You are live').then(r => { if (r) { loadMatches(); loadDetail(m.id); } });
  };
  const end = (m) => {
    if (!window.confirm('End the broadcast? This cannot be restarted — the recording will be linked to the match.')) return;
    act('end', () => api.post(`/youtube/matches/${m.id}/end`), 'Broadcast ended — recording linked to the scorecard').then(r => { if (r) { loadMatches(); loadDetail(m.id); } });
  };
  const newKey = (m) => {
    if (!window.confirm('Issue a new stream key? The old key stops working — the camera team will need the new one.')) return;
    act('key', () => api.post(`/youtube/matches/${m.id}/new-key`), 'New stream key issued').then(r => { if (r) loadDetail(m.id); });
  };
  const remove = (m) => {
    if (!window.confirm('Delete this broadcast from YouTube?')) return;
    act('delete', () => api.delete(`/youtube/matches/${m.id}/broadcast`), 'Broadcast removed').then(r => { if (r) { setDetail(null); loadMatches(); } });
  };

  const whatsapp = (m, c) => [
    `*UCC LIVE — ${teams(m)}*`,
    `${fmtWhen(c.scheduled_at)}`,
    '',
    `Server URL: ${c.server_url}`,
    c.backup_url ? `Backup URL: ${c.backup_url}` : null,
    `Stream key: ${c.stream_key}`,
    '',
    `Start sending 10 minutes before. Watch: ${c.watch_url}`,
  ].filter(x => x !== null).join('\n');

  const connected = status?.connected;

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '16px 14px 90px' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1, color: '#6b7280' }}>ADMIN</div>
      <h1 style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, color: navy, margin: '2px 0 14px' }}>📺 YouTube Live</h1>

      {/* Connection */}
      <div style={card}>
        {!status ? 'Loading…' : status.error ? <span style={{ color: '#dc2626' }}>{status.error}</span>
          : !status.configured ? (
            <div style={{ fontSize: '0.85rem' }}>The server isn't set up for YouTube yet — add <code>YOUTUBE_CLIENT_ID</code> and <code>YOUTUBE_CLIENT_SECRET</code> in Railway.</div>
          ) : connected ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 800, color: '#16a34a' }}>✓ Connected to “{status.channel_title}”</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  by {status.connected_by || '—'} · {fmtWhen(status.connected_at)}
                  {status.last_error && <span style={{ color: '#b45309' }}> · last problem: {status.last_error}</span>}
                </div>
              </div>
              <button onClick={disconnect} disabled={!!busy} style={btn('white', '#dc2626', '1px solid #fecaca')}>Disconnect</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: '0.85rem' }}>
                Connect the club's YouTube channel once. Sign in with the <strong>UCC Google account</strong> that owns the channel.
              </div>
              <button onClick={connect} disabled={!!busy} style={btn('#dc2626')}>{busy === 'connect' ? 'Opening Google…' : '▶ Connect YouTube'}</button>
            </div>
          )}
      </div>

      {/* Matches */}
      <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#6b7280', margin: '6px 2px 8px' }}>MATCHES — LAST 7 DAYS AND NEXT 30</div>
      {matches.length === 0 && <div style={{ ...card, color: '#6b7280', fontSize: '0.85rem' }}>No matches in this window.</div>}
      {matches.map(m => {
        const isOpen = openId === m.id;
        const c = detail?.camera;
        return (
          <div key={m.id} style={{ ...card, padding: 0, overflow: 'hidden', borderColor: isOpen ? navy : '#e5e7eb' }}>
            <div onClick={() => open(m)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, color: navy }}>{fmtDate(m.match_date)} · {teams(m)}</div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                  {m.series_name || m.match_type}{m.live_scheduled_at ? ` · starts ${fmtWhen(m.live_scheduled_at)}` : ''}
                </div>
              </div>
              <StatusChip status={m.live_status} />
            </div>

            {isOpen && (
              <div style={{ borderTop: '1px solid #e5e7eb', padding: 14, background: '#fafafa' }}>
                {!connected ? (
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Connect the YouTube channel above first.</div>
                ) : !m.has_stream ? (
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 6 }}>Schedule a public broadcast</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)}
                        style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8 }} />
                      <button onClick={() => create(m)} disabled={!!busy || !when} style={btn(navy)}>
                        {busy === 'create' ? 'Creating…' : 'Create broadcast & stream key'}
                      </button>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 6 }}>
                      The title is made from the teams and date. It appears on the dashboard's LIVE card straight away.
                    </div>
                  </div>
                ) : !detail ? 'Loading…' : (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <StatusChip status={detail.status} />
                      {detail.camera?.watch_url && (
                        <a href={detail.camera.watch_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', fontWeight: 700 }}>Open on YouTube ↗</a>
                      )}
                    </div>
                    {detail.youtube_error && <div style={{ color: '#b45309', fontSize: '0.8rem', marginTop: 6 }}>{detail.youtube_error}</div>}
                    <HealthDot b={detail} />

                    {detail.status !== 'complete' && c && (
                      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '6px 12px', marginBottom: 12 }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 800, color: navy, margin: '6px 0 2px' }}>FOR THE CAMERA TEAM</div>
                        <CopyRow label="Server URL" value={c.server_url} />
                        <CopyRow label="Backup URL" value={c.backup_url} />
                        <CopyRow label="Stream key" value={c.stream_key} secret />
                        <CopyRow label="Starts" value={fmtWhen(c.scheduled_at)} />
                        <div style={{ padding: '8px 0' }}>
                          <button onClick={() => navigator.clipboard.writeText(whatsapp(m, c)).then(() => toast.success('Copied — paste into WhatsApp'))}
                            style={btn('#16a34a')}>📋 Copy all for WhatsApp</button>
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {detail.status === 'created' && (
                        <button onClick={() => goLive(m)} disabled={!!busy} style={btn('#dc2626')}>
                          {busy === 'live' ? 'Going live…' : '🔴 Go live'}
                        </button>
                      )}
                      {detail.status === 'live' && (
                        <button onClick={() => end(m)} disabled={!!busy} style={btn('#111827')}>
                          {busy === 'end' ? 'Ending…' : '⏹ End broadcast'}
                        </button>
                      )}
                      {detail.status === 'created' && (
                        <button onClick={() => newKey(m)} disabled={!!busy} style={btn('white', navy, '1px solid #d1d5db')}>New stream key</button>
                      )}
                      {detail.status !== 'live' && (
                        <button onClick={() => remove(m)} disabled={!!busy} style={btn('white', '#dc2626', '1px solid #fecaca')}>
                          {detail.status === 'complete' ? 'Clear' : 'Delete broadcast'}
                        </button>
                      )}
                    </div>
                    {detail.status === 'created' && (
                      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 8 }}>
                        Go live works once the dot above turns green — that means the camera's video is reaching YouTube.
                      </div>
                    )}
                    {detail.status === 'complete' && (
                      <div style={{ fontSize: '0.78rem', color: '#374151', marginTop: 8 }}>
                        The recording is linked to this match and shows on its scorecard (YouTube may take a little while to finish processing it).
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
